@if (room(); as room) {
  <div class="min-h-screen bg-linear-to-b from-[#08101d] to-[#0b1020] text-indigo-50 p-8">
    <div class="max-w-screen-xl mx-auto space-y-6">

      <!-- Hero -->
      <section class="grid grid-cols-1 lg:grid-cols-[1.4fr_0.9fr] gap-6">
        <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 backdrop-blur-sm p-7 flex flex-col gap-4 min-h-55">
          <p class="text-[#58e1c1] text-xs font-bold uppercase tracking-widest">Token consumption</p>
          <h1 class="text-3xl font-bold tracking-tight leading-tight">{{ room.roomName }}</h1>
          <p class="text-slate-400 text-sm leading-relaxed">
            Overview of <strong class="text-indigo-200">{{ room.totals.calls }}</strong> calls —
            contrast between <strong class="text-indigo-200">prompt volume</strong>
            and <strong class="text-indigo-200">completion volume</strong> by source.
          </p>
          <div class="flex flex-col gap-2 mt-auto">
            <button
              type="button"
              class="self-start inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold bg-[#58e1c1]/10 text-[#58e1c1] border border-[#58e1c1]/30 hover:bg-[#58e1c1]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              [disabled]="isExporting()"
              (click)="exportContext()"
              aria-label="Export AI context as Markdown file"
            >
              @if (isExporting()) {
                <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Exporting…
              } @else {
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"/>
                </svg>
                Export AI context
              }
            </button>
            @if (contextUnavailable()) {
              <p class="text-slate-400 text-xs" role="alert">No context available yet — the AI agent may not have joined yet.</p>
            }
            @if (contextFreshnessLabel()) {
              <p class="text-slate-500 text-xs">{{ contextFreshnessLabel() }}</p>
            }
          </div>
        </div>
        <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 backdrop-blur-sm p-6 grid gap-3 content-center">
          <div class="rounded-[18px] bg-white/[0.03] border border-white/[0.08] px-5 py-4">
            <p class="text-slate-400 text-xs uppercase tracking-wide mb-1">Total tokens</p>
            <p class="text-3xl font-extrabold tracking-tight">{{ totalTokens() | number }}</p>
          </div>
          <div class="rounded-[18px] bg-white/[0.03] border border-white/[0.08] px-5 py-4">
            <p class="text-slate-400 text-xs uppercase tracking-wide mb-1">Calls</p>
            <p class="text-3xl font-extrabold tracking-tight">{{ room.totals.calls }}</p>
          </div>
          <div class="rounded-[18px] bg-white/[0.03] border border-white/[0.08] px-5 py-4">
            <p class="text-slate-400 text-xs uppercase tracking-wide mb-1">Dominant source</p>
            <p class="text-3xl font-extrabold tracking-tight capitalize">{{ dominantSource() }}</p>
          </div>
        </div>
      </section>

      <!-- Stat cards -->
      <section class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 p-6">
          <p class="text-slate-400 text-sm mb-2">Prompt tokens</p>
          <p class="text-4xl font-extrabold tracking-tight mb-2">{{ room.totals.promptTokens | number }}</p>
          <p class="text-slate-400 text-sm leading-relaxed">{{ promptPercent() }}% of total volume</p>
        </div>
        <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 p-6">
          <p class="text-slate-400 text-sm mb-2">Completion tokens</p>
          <p class="text-4xl font-extrabold tracking-tight mb-2">{{ room.totals.completionTokens | number }}</p>
          <p class="text-slate-400 text-sm leading-relaxed">{{ completionPercent() }}% of total volume</p>
        </div>
        <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 p-6">
          <p class="text-slate-400 text-sm mb-2">Average per call</p>
          <p class="text-4xl font-extrabold tracking-tight mb-2">{{ avgPerCall() | number }}</p>
          <p class="text-slate-400 text-sm leading-relaxed">Across all calls in this room</p>
        </div>
        <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 p-6">
          <p class="text-slate-400 text-sm mb-2">Prompt / completion ratio</p>
          <p class="text-4xl font-extrabold tracking-tight mb-2">{{ ratio() }}</p>
          <p class="text-slate-400 text-sm leading-relaxed">System consumes more input than it outputs</p>
        </div>
      </section>

      <!-- Charts -->
      <section class="grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-4">
        <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 p-6">
          <h2 class="text-xl font-semibold tracking-tight mb-1">Evolution per call</h2>
          <p class="text-slate-400 text-sm mb-4">Prompt peaks come from chat calls. Completions stay more regular on the vision side.</p>
          <app-line-chart [seriesA]="lineSeriesA()" [seriesB]="lineSeriesB()" />
          <div class="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-slate-400 text-sm">
            <span class="inline-flex items-center gap-2">
              <span class="w-2.5 h-2.5 rounded-full bg-[#7c9cff]" aria-hidden="true"></span>
              Prompt tokens
            </span>
            <span class="inline-flex items-center gap-2">
              <span class="w-2.5 h-2.5 rounded-full bg-[#58e1c1]" aria-hidden="true"></span>
              Completion tokens
            </span>
          </div>
        </div>
        <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 p-6">
          <h2 class="text-xl font-semibold tracking-tight mb-1">Breakdown by source</h2>
          <p class="text-slate-400 text-sm mb-4">Vision dominates completions. Chat concentrates almost all prompt cost.</p>
          <app-bar-chart [labels]="barLabels()" [seriesA]="barSeriesA()" [seriesB]="barSeriesB()" />
          <div class="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-slate-400 text-sm">
            <span class="inline-flex items-center gap-2">
              <span class="w-2.5 h-2.5 rounded-full bg-[#7c9cff]" aria-hidden="true"></span>
              Prompt
            </span>
            <span class="inline-flex items-center gap-2">
              <span class="w-2.5 h-2.5 rounded-full bg-[#ffb86b]" aria-hidden="true"></span>
              Completion
            </span>
          </div>
        </div>
      </section>

      <!-- Compression section -->
      <div class="flex items-center gap-4 pt-2">
        <p class="text-[#58e1c1] text-xs font-bold uppercase tracking-widest whitespace-nowrap">Context compression</p>
        <div class="flex-1 h-px bg-white/10"></div>
      </div>

      @if (hasCompression()) {
        <!-- Compression stat cards -->
        <section class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 p-6">
            <p class="text-slate-400 text-sm mb-2">Chars saved</p>
            <p class="text-4xl font-extrabold tracking-tight mb-2">{{ totalCharsSaved() | number }}</p>
            <p class="text-slate-400 text-sm leading-relaxed">Total reduction across all compression batches</p>
          </div>
          <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 p-6">
            <p class="text-slate-400 text-sm mb-2">Avg transcript ratio</p>
            <p class="text-4xl font-extrabold tracking-tight mb-2">{{ avgTranscriptRatio() }}</p>
            <p class="text-slate-400 text-sm leading-relaxed">Average reduction per transcript batch</p>
          </div>
          <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 p-6">
            <p class="text-slate-400 text-sm mb-2">Avg vision ratio</p>
            <p class="text-4xl font-extrabold tracking-tight mb-2">{{ avgVisionRatio() }}</p>
            <p class="text-slate-400 text-sm leading-relaxed">Average reduction per vision batch</p>
          </div>
        </section>

        <!-- Raw vs compressed charts -->
        <section class="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 p-6">
            <h2 class="text-xl font-semibold tracking-tight mb-1">Transcript — raw vs compressed</h2>
            <p class="text-slate-400 text-sm mb-4">Tokens before and after compression per batch.</p>
            <app-bar-chart
              [labels]="transcriptBatchLabels()"
              [seriesA]="transcriptRawSeries()"
              [seriesB]="transcriptCompressedSeries()" />
            <div class="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-slate-400 text-sm">
              <span class="inline-flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-[#ffb86b]" aria-hidden="true"></span>
                Raw tokens
              </span>
              <span class="inline-flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-[#58e1c1]" aria-hidden="true"></span>
                Compressed tokens
              </span>
            </div>
          </div>
          <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 p-6">
            <h2 class="text-xl font-semibold tracking-tight mb-1">Vision — raw vs compressed</h2>
            <p class="text-slate-400 text-sm mb-4">Tokens before and after compression per batch.</p>
            <app-bar-chart
              [labels]="visionBatchLabels()"
              [seriesA]="visionRawSeries()"
              [seriesB]="visionCompressedSeries()" />
            <div class="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-slate-400 text-sm">
              <span class="inline-flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-[#ffb86b]" aria-hidden="true"></span>
                Raw tokens
              </span>
              <span class="inline-flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-[#58e1c1]" aria-hidden="true"></span>
                Compressed tokens
              </span>
            </div>
          </div>
        </section>

        <!-- Context composition + total context size -->
        <section class="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 p-6">
            <h2 class="text-xl font-semibold tracking-tight mb-1">Context composition</h2>
            <p class="text-slate-400 text-sm mb-4">
              Share of each section at each compression event — stacked to 100%.
            </p>
            <app-stacked-bar-chart [labels]="proportionLabels()" [series]="proportionSeries()" />
            <div class="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-slate-400 text-sm">
              @for (s of proportionSeries(); track s.label) {
                <span class="inline-flex items-center gap-2">
                  <span class="w-2.5 h-2.5 rounded-full" [style.background]="s.color" aria-hidden="true"></span>
                  {{ s.label }}
                </span>
              }
            </div>
          </div>
          <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 p-6">
            <h2 class="text-xl font-semibold tracking-tight mb-1">Total context size</h2>
            <p class="text-slate-400 text-sm mb-4">
              Total context tokens at each compression event (all sections combined).
            </p>
            <app-line-chart [seriesA]="totalContextTokensSeries()" [seriesB]="[]" />
            <div class="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-slate-400 text-sm">
              <span class="inline-flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-[#7c9cff]" aria-hidden="true"></span>
                Total context (tokens)
              </span>
            </div>
          </div>
        </section>
      } @else {
        <div class="rounded-3xl border border-white/10 bg-linear-to-b from-white/3 to-white/1 p-8 flex items-center gap-4 text-slate-400 text-sm">
          <span class="text-2xl">⏳</span>
          <span>No compression events yet — charts will appear once the session triggers its first context compression.</span>
        </div>
      }

    </div>
  </div>
} @else {
  <div class="min-h-screen bg-linear-to-b from-[#08101d] to-[#0b1020] flex items-center justify-center">
    <p class="text-slate-400 text-sm">Loading metrics…</p>
  </div>
}
